<template>
  <div class="tp-mini">
    <div v-if="!layout || layout.rows.length === 0" class="tp-mini__empty">
      {{ emptyText }}
    </div>

    <div v-else class="tp-mini__grid">
      <div v-for="(row, rowIndex) in layout.rows" :key="rowIndex" class="tp-mini__row">
        <div
          v-for="(cell, cellIndex) in row.cells"
          :key="`${rowIndex}-${cellIndex}`"
          class="tp-mini__col"
          :style="{ width: `${widthPercents(row.widths)[cellIndex]}%` }"
        >
          <button
            type="button"
            class="tp-mini__cell"
            :class="cellClass(cell)"
            :disabled="!isSelectable(cell)"
            :title="cellTitle(cell)"
            @click="onCellClick(cell)"
          >
            <span class="tp-mini__cell-head">
              <span
                v-for="marker in markersOf(cell)"
                :key="marker.symbol"
                class="tp-mini__symbol"
                :class="{
                  'tp-mini__symbol--reference': marker.mode === 'reference',
                  'tp-mini__symbol--broken': marker.broken,
                }"
              >
                {{ marker.symbol }}
              </span>
              <span class="tp-mini__name">{{ cell.name || "（名称未設定）" }}</span>
            </span>
            <span class="tp-mini__meta">
              <span>{{ cellTypeLabels[cell.type] }}</span>
              <span v-if="cell.required" class="tp-mini__required">必須</span>
              <span v-if="!cell.visible">Excel非出力</span>
              <span v-if="hasBroken(cell)" class="tp-mini__broken">要確認</span>
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import {
  NurseryTeachingPlanLayout,
  NurseryTeachingPlanLayoutCell,
  NurseryTeachingPlanTransferRuleModeType,
} from "chaild-api/lib";
import TeachingPlanUtil from "@/utils/TeachingPlanUtil";
import { cellTypeLabels } from "@/constants/teachingPlan";

/** 転記ルールの対応記号 (転記元・転記先の両グリッドに同じ記号を出す) */
export type TeachingPlanMiniGridMarker = {
  symbol: string;
  mode: NurseryTeachingPlanTransferRuleModeType;
  broken: boolean;
};

/**
 * レイアウトを行×列のまま縮小描画し、セルを選べるようにしたグリッド。
 *
 * 転記ルールのマトリクスビルダー (renewal-plan.md C-4) で転記元・転記先を
 * 左右に並べるために使う。フラットなリストにすると「どの位置の項目か」が
 * 失われて対応づけが読み取れなくなるため、実際の行×列を保つ。
 */
export default defineComponent({
  name: "TeachingPlanMiniGrid",

  props: {
    layout: {
      type: [Object, null] as PropType<NurseryTeachingPlanLayout | null>,
      default: null,
    },
    /**
     * itemKey -> 対応記号。両グリッドで同じ記号が同じルールを指す。
     * 転記元は複数の転記先に使えるため (サーバの一意制約は転記先のみ) 配列で持つ。
     */
    markers: {
      type: Object as PropType<{ [itemKey: string]: TeachingPlanMiniGridMarker[] }>,
      default: () => ({}),
    },
    /** クリックで選択中のセル */
    selectedItemKey: {
      type: [String, null] as PropType<string | null>,
      default: null,
    },
    /** ルール行のホバー等で対になっているセルを強調する */
    activeItemKey: {
      type: [String, null] as PropType<string | null>,
      default: null,
    },
    /**
     * すでに転記先として使われているセル。
     * 選べないわけではなく「選ぶと置き換わる」ので、淡くして選択不可に
     * 見せるのではなく、置き換えになることを説明する。
     */
    mutedItemKeys: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    emptyText: {
      type: String,
      default: "レイアウトがありません。",
    },
  },

  emits: ["select"],

  data() {
    return {
      cellTypeLabels,
    };
  },

  methods: {
    widthPercents(widths: number[]): number[] {
      return TeachingPlanUtil.widthPercents(widths);
    },
    /** 見出しセルは値を持たないため転記の対象外 */
    isSelectable(cell: NurseryTeachingPlanLayoutCell): boolean {
      return !this.disabled && !!cell.itemKey && cell.type !== "label";
    },
    markersOf(cell: NurseryTeachingPlanLayoutCell): TeachingPlanMiniGridMarker[] {
      return cell.itemKey ? this.markers[cell.itemKey] || [] : [];
    },
    hasBroken(cell: NurseryTeachingPlanLayoutCell): boolean {
      return this.markersOf(cell).some((marker) => marker.broken);
    },
    cellClass(cell: NurseryTeachingPlanLayoutCell): Record<string, boolean> {
      const markers = this.markersOf(cell);
      return {
        "tp-mini__cell--label": cell.type === "label",
        "tp-mini__cell--reference": cell.type === "reference",
        "tp-mini__cell--selected":
          !!cell.itemKey && this.selectedItemKey === cell.itemKey,
        "tp-mini__cell--active": !!cell.itemKey && this.activeItemKey === cell.itemKey,
        "tp-mini__cell--mapped": markers.length > 0,
        "tp-mini__cell--broken": markers.some((marker) => marker.broken),
        "tp-mini__cell--muted":
          !!cell.itemKey && this.mutedItemKeys.includes(cell.itemKey),
      };
    },
    cellTitle(cell: NurseryTeachingPlanLayoutCell): string {
      if (cell.type === "label") {
        return "見出しセルは転記の対象外です";
      }
      const base = cell.itemKey ? `${cell.name}（${cell.itemKey}）` : cell.name;
      if (cell.itemKey && this.mutedItemKeys.includes(cell.itemKey)) {
        return `${base}／すでに転記先として設定済み。選ぶと置き換わります`;
      }
      return base;
    },
    onCellClick(cell: NurseryTeachingPlanLayoutCell) {
      if (!this.isSelectable(cell) || !cell.itemKey) {
        return;
      }
      this.$emit("select", cell.itemKey);
    },
  },
});
</script>

<style scoped>
.tp-mini {
  background-color: #fafafa;
  padding: 6px;
  border-radius: 4px;
  overflow-x: auto;
}
.tp-mini__empty {
  padding: 24px 8px;
  text-align: center;
  font-size: 12px;
  color: #90a4ae;
}
.tp-mini__grid {
  border-top: 1px solid #e0e0e0;
  border-left: 1px solid #e0e0e0;
  background-color: #ffffff;
  min-width: 280px;
}
.tp-mini__row {
  display: flex;
  align-items: stretch;
}
.tp-mini__col {
  display: flex;
  min-width: 0;
}
.tp-mini__col > * {
  flex: 1;
}
.tp-mini__cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-height: 46px;
  padding: 5px 6px;
  border-right: 1px solid #e0e0e0;
  border-bottom: 1px solid #e0e0e0;
  background-color: #ffffff;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.12s ease;
}
.tp-mini__cell:hover:not(:disabled) {
  background-color: #f1f8fb;
}
.tp-mini__cell:disabled {
  cursor: default;
}
.tp-mini__cell--label {
  background-color: #f5f7f8;
}
.tp-mini__cell--reference {
  background-color: #fbfdff;
}
.tp-mini__cell--mapped {
  background-color: #eef6fb;
}
/*
 * 設定済みの転記先。淡くすると選択不可に見えるが、実際は選べて置き換わる。
 * 明度は落とさず、設定済みであることだけを罫線で示す。
 */
.tp-mini__cell--muted {
  box-shadow: inset 0 0 0 1px #b3e5fc;
}
.tp-mini__cell--selected {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: -2px;
  background-color: #e3f2fd;
}
.tp-mini__cell--active {
  outline: 2px dashed #ef6c00;
  outline-offset: -2px;
}
.tp-mini__cell--broken {
  background-color: #fff8e1;
}
.tp-mini__cell-head {
  display: flex;
  align-items: flex-start;
  gap: 4px;
}
.tp-mini__symbol {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border-radius: 4px;
  background-color: #cfe8f5;
  color: #01579b;
  font-size: 11px;
  font-weight: 700;
  line-height: 18px;
}
.tp-mini__symbol--reference {
  background-color: #1565c0;
  color: #ffffff;
}
.tp-mini__symbol--broken {
  background-color: #ef6c00;
  color: #ffffff;
}
.tp-mini__name {
  font-size: 12px;
  font-weight: 600;
  color: #263238;
  overflow-wrap: break-word;
  word-break: break-word;
}
.tp-mini__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  font-size: 10px;
  color: #78909c;
}
.tp-mini__required {
  color: #c62828;
}
.tp-mini__broken {
  color: #ef6c00;
  font-weight: 700;
}
</style>
